import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { Form, Input, Button, Typography, App } from "antd";
import { GradeEntityInput } from "../../../services/grade/grade.types";
import { useLocation, useNavigate } from "react-router-dom";
import "./subjectForm.scss";
import {
  createGrade,
  getGradeById,
  updateGrade,
} from "../../../services/grade/grade.service";
import ActivityIndicator from "../../ActivityIndicator";
import { useDispatch } from "react-redux";
import { handleSubmitFormResponseManage } from "../../../utils";
import { initialValuesGrade } from "./utils";

const { Title } = Typography;

const GradeForm: React.FC = () => {
  const { notification } = App.useApp();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const gradeId = location?.state as number;
  const [initialValues, setinitialValues] =
    useState<GradeEntityInput>(initialValuesGrade);
  // Synchronous re-entrancy guard: `loading` lags a render behind, so a fast
  // double click on Guardar would fire two create/update calls.
  const submitInFlightRef = useRef(false);

  const fetchInfo = async () => {
    setLoading(true);
    try {
      if (gradeId) {
        const response = await getGradeById(gradeId);
        // Services are being migrated to throw on error instead of
        // returning null — treat a falsy result as a failed load either way.
        if (!response) {
          throw new Error("No se pudo cargar el grado.");
        }
        setinitialValues({
          grade: response.grade ?? "",
          code: response.code ?? "",
        });
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setLoadFailed(true);
      console.error(error);
      const backendMessage = error?.response?.data?.message;
      notification.error({
        message: "Error",
        description: backendMessage
          ? `No se pudo cargar la información. Reintentá o volvé al listado.\n${backendMessage}`
          : "No se pudo cargar la información. Reintentá o volvé al listado.",
        placement: "top",
      });
    }
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  const handleSubmit = async () => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      await submitGrade();
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const submitGrade = async () => {
    setLoading(true);
    try {
      await handleSubmitFormResponseManage(
        initialValues,
        createGrade,
        updateGrade,
        navigate,
        dispatch,
        "grade",
        "addGrade",
        "updateGrade",
        gradeId
      );
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      notification.error({
        message: "Error",
        description: "Ocurrió un error al guardar la asignatura.",
        placement: "top",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return <ActivityIndicator />;
  }

  return (
    <div className="subjectFormContainer">
      {gradeId ? (
        <Title level={3}>Editar Grado</Title>
      ) : (
        <Title level={3}>Crear Grado</Title>
      )}

      <Form
        initialValues={initialValues}
        layout="vertical"
        onFinish={handleSubmit}
        className="subjectForm"
      >
        <Form.Item
          label="Grado"
          name="grade"
          rules={[
            { required: true, message: "El nombre del grado es requerido" },
          ]}
        >
          <Input
            placeholder="Ej. 1° Primaria"
            value={initialValues.grade}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setinitialValues({
                ...initialValues,
                grade: e.target.value,
              })
            }
          />
        </Form.Item>

        <Form.Item
          label="Código"
          name="code"
          rules={[
            { required: true, message: "El código del grado es requerido" },
          ]}
        >
          <Input
            placeholder="Ej. 1P, 2P, 3P"
            value={initialValues.code}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setinitialValues({
                ...initialValues,
                code: e.target.value,
              })
            }
          />
        </Form.Item>

        <Form.Item>
          <div className="formButtons">
            <Button htmlType="button" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" disabled={loadFailed}>
              Guardar
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};

export default GradeForm;
